import OlPoint from 'ol/geom/Point'
import OlLineString from 'ol/geom/LineString'
import OlPolygon from 'ol/geom/Polygon'
import OlMultiPoint from 'ol/geom/MultiPoint'
import OlMultiLineString from 'ol/geom/MultiLineString'
import OlMultiPolygon from 'ol/geom/MultiPolygon'
import {getLength} from './OL'

import {CoordinateSystem} from '../../enums/CoordinateSystem'

export class Point extends OlPoint {
  public cs: CoordinateSystem = CoordinateSystem.WEB_MERCATOR
  public x() {
    return this.getCoordinates()[0]
  }
  public y() {
    return this.getCoordinates()[1]
  }
  transform(source: CoordinateSystem, destination: CoordinateSystem): this {
    this.cs = destination
    return super.transform(source, destination) as this
  }
  trans(destination: CoordinateSystem): Point {
    if (this.cs == destination) {
      return this
    }
    return this.clone().transform(this.cs, destination)
  }

  getCoordinates(): number[] {
    return super.getCoordinates()
  }
  getCenter(): Point {
    return new Point(this.getCoordinates())
  }
  clone(): Point {
    return new Point(this.getCoordinates())
  }

  getType(): any {
    //GeometryType
    return super.getType()
  }
}

export class LineString extends OlLineString {
  public cs: CoordinateSystem = CoordinateSystem.WEB_MERCATOR
  transform(source: CoordinateSystem, destination: CoordinateSystem): this {
    this.cs = destination
    return super.transform(source, destination) as this
  }
  trans(destination: CoordinateSystem): LineString {
    return this.clone().transform(this.cs, destination)
  }
  getCenter(): Point {
    return new Point(this.getCoordinateAt(0.5))
  }
  clone(): LineString {
    return new LineString(this.getCoordinates())
  }
  getGeodeticLength(): number {
    return this._getGeodeticLength(this)
  }
  getType(): any {
    return super.getType()
  }
  protected _getGeodeticLength(lineString: LineString): number {
    return getLength(lineString)
  }
}

export class Polygon extends OlPolygon {
  public cs: CoordinateSystem = CoordinateSystem.WEB_MERCATOR
  transform(source: CoordinateSystem, destination: CoordinateSystem): this {
    this.cs = destination
    return super.transform(source, destination) as this
  }
  trans(destination: CoordinateSystem): Polygon {
    return this.clone().transform(this.cs, destination)
  }
  getType(): any {
    return super.getType()
  }
  getCenter(): Point {
    return new Point(this.getInteriorPoint().getCoordinates()) as Point
  }
  clone(): Polygon {
    return new Polygon(this.getCoordinates())
  }
}

export class MultiPoint extends OlMultiPoint {
  public cs: CoordinateSystem = CoordinateSystem.WEB_MERCATOR
  transform(source: CoordinateSystem, destination: CoordinateSystem): this {
    this.cs = destination
    return super.transform(source, destination) as this
  }
  trans(destination: CoordinateSystem): MultiPoint {
    return this.clone().transform(this.cs, destination)
  }
  getType(): any {
    //GeometryType
    return super.getType()
  }
  getCenter(): Point {
    throw new Error('not implemented')
  }
  clone(): MultiPoint {
    return new MultiPoint(this.getCoordinates())
  }
}

export class MultiLineString extends OlMultiLineString {
  public cs: CoordinateSystem = CoordinateSystem.WEB_MERCATOR
  transform(source: CoordinateSystem, destination: CoordinateSystem): this {
    this.cs = destination
    return super.transform(source, destination) as this
  }
  trans(destination: CoordinateSystem) {
    return this.clone().transform(this.cs, destination)
  }
  getType(): any {
    //GeometryType
    return super.getType()
  }
  getCenter(): Point {
    const coords = this.getCoordinates()
    if (coords.length > 0) {
      return new LineString(coords[0]).getCenter()
    } else {
      throw new Error('No geometry')
    }
  }
  clone(): MultiLineString {
    return new MultiLineString(this.getCoordinates())
  }
}
export class MultiPolygon extends OlMultiPolygon {
  public cs: CoordinateSystem = CoordinateSystem.WEB_MERCATOR
  transform(source: CoordinateSystem, destination: CoordinateSystem): this {
    this.cs = destination
    return super.transform(source, destination) as this
  }
  trans(destination: CoordinateSystem) {
    return this.clone().transform(this.cs, destination)
  }
  getType(): any {
    //GeometryType
    return super.getType()
  }
  getCenter(): Point {
    const coords = this.getCoordinates()
    if (coords.length == 1) {
      return new Polygon(coords[0]).getInteriorPoint() as Point
    }
    return new Extent(this.getExtent()).getCenter()
  }
  clone(): MultiPolygon {
    return new MultiPolygon(this.getCoordinates())
  }

  // create a function that will return multipoint for each polygon center in the multipolygon
  getCenters(): MultiPoint {
    const centers = this.getPolygons().map(polygon => polygon.getInteriorPoint().getCoordinates())
    return new MultiPoint(centers)
  }
}

export class Extent extends LineString {
  /**
   * x is like in math - its horizontal axis
   * @param extent
   */
  constructor(extent: number[]) {
    super([
      [extent[0], extent[1]],
      [extent[2], extent[3]]
    ])
  }
  clone(): Extent {
    return new Extent(this.getExtent())
  }
  getXMin(): number {
    return this.getExtent()[0]
  }
  getXMax(): number {
    return this.getExtent()[2]
  }
  getYMin(): number {
    return this.getExtent()[1]
  }
  getYMax(): number {
    return this.getExtent()[3]
  }
  getWidth(): number {
    return this.getXMax() - this.getXMin()
  }
  getHeight(): number {
    return this.getYMax() - this.getYMin()
  }
  getGeodeticWidth(): number {
    const y = this.getYMin() + this.getHeight() / 2
    const line = new LineString([
      [this.getXMin(), y],
      [this.getXMax(), y]
    ])
    return this._getGeodeticLength(line)
  }
  getGeodeticHeight(): number {
    const x = this.getXMin() + this.getWidth() / 2
    const line = new LineString([
      [x, this.getYMin()],
      [x, this.getYMax()]
    ])
    return this._getGeodeticLength(line)
  }
  setXMin(xMin: number): void {
    const coords = this.getCoordinates()
    coords[0][0] = xMin
    this.setCoordinates(coords)
  }
  setXMax(xMax: number): void {
    const coords = this.getCoordinates()
    coords[1][0] = xMax
    this.setCoordinates(coords)
  }
  setYMin(yMin: number): void {
    const coords = this.getCoordinates()
    coords[0][1] = yMin
    this.setCoordinates(coords)
  }
  setYMax(yMax: number): void {
    const coords = this.getCoordinates()
    coords[1][1] = yMax
    this.setCoordinates(coords)
  }
  getPolygon(): Polygon {
    const extentArray = this.getExtent()
    const extentPoint = [
      [extentArray[0], extentArray[1]],
      [extentArray[0], extentArray[3]],
      [extentArray[2], extentArray[3]],
      [extentArray[2], extentArray[1]],
      [extentArray[0], extentArray[1]]
    ] as [number, number][]
    return new Polygon([extentPoint])
  }
}
export type Geometry = Point | LineString | Polygon | MultiPoint | MultiLineString | MultiPolygon

export type Coordiantes = [number, number] | [number, number][] | [number, number][][] | [number, number][][][]
